import React from "react";
import axios from "axios";

//Limitless imports
import { log } from "@limitless/common/utils";
import { createPublicAPIRequest } from "@limitless/common/utils/PublicAPIUtils";
import { TOWNS_JSON, fetchPlatformResources } from "@limitless/common/apis/resource-api";
import { INITIAL_FIELD, INITIAL_BOOL_FIELD, INITIAL_OBJECT_FIELD, getAllFieldValues } from "@limitless/expert/utils/FieldValidators";
import { createUserPool } from "@limitless/expert/apis/authentication-api/AuthenticationAPI";
import { validateSignUpFields } from "./SignUpValidator";
import { signUpUser } from "@limitless/expert/apis/sign-up-api/SignUpAPI";
export const FIELDS = {
  EMAIL: "email",
  PASSWORD: "password",
  FIRST_NAME: "firstName",
  LAST_NAME: "lastName",
  COUNTRY: "country",
  TOWN: "town",
  TERMS_OF_SERVICE: "termsOfService",
  CODE_OF_CONDUCT_TERMS: "codeOfConductTerms",
  PRIVACY_TERMS: "privacyTerms",
  AGE_TERMS: "ageTerms",
  NEWS_LETTER: "newsLetter",
  SHOW_PASSWORD: "showPassword"
};
const INITIAL_SIGN_UP_STATE = {
  [FIELDS.EMAIL]: INITIAL_FIELD,
  [FIELDS.PASSWORD]: INITIAL_FIELD,
  [FIELDS.FIRST_NAME]: INITIAL_FIELD,
  [FIELDS.LAST_NAME]: INITIAL_FIELD,
  [FIELDS.COUNTRY]: INITIAL_OBJECT_FIELD,
  [FIELDS.TOWN]: INITIAL_OBJECT_FIELD,
  [FIELDS.TERMS_OF_SERVICE]: INITIAL_BOOL_FIELD,
  [FIELDS.AGE_TERMS]: INITIAL_BOOL_FIELD,
  [FIELDS.NEWS_LETTER]: {
    value: false,
    pristine: false
  }
};
const getExpertElibility = async email => {
  if (!email) {
    throw new Error("email not found");
  }
  const apiConfig = createPublicAPIRequest("put", "eligibility", null, {
    email
  }, null);
  return await axios(apiConfig);
};
export const useSignUp = groupHash => {
  var _values$country$value3, _values$country3, _values$country3$valu;
  const [values, setValues] = React.useState(INITIAL_SIGN_UP_STATE);
  const [towns, setTowns] = React.useState([]);
  const [stateSelectionRequired, setStateSelectionRequired] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = React.useState(false);
  const [showOverlay, setShowOverlay] = React.useState(false);
  const [verifySignUp, setVerifySignUp] = React.useState(false);
  const [townsResourceLoading, setTownsResourceLoading] = React.useState(false);
  const loadResources = locale => {
    fetchPlatformResources({
      locale,
      resourceList: [TOWNS_JSON]
    }, processTownsResource, processTownsResource);
  };
  const disableTermsStyle = React.useMemo(() => {
    var _values$country$value, _values$country$value2;
    return townsResourceLoading || !((_values$country$value = values.country.value) !== null && _values$country$value !== void 0 && _values$country$value.value) || stateSelectionRequired && ((_values$country$value2 = values.country.value) === null || _values$country$value2 === void 0 ? void 0 : _values$country$value2.value);
  }, [(_values$country$value3 = values.country.value) === null || _values$country$value3 === void 0 ? void 0 : _values$country$value3.value, stateSelectionRequired, townsResourceLoading]);
  const handleStateChange = obj => {
    Object.keys(obj).forEach(key => {
      obj[key] = {
        value: obj[key],
        pristine: false
      };
    });
    setValues({
      ...values,
      ...obj
    });
    setLoading(false);
  };
  const processTownsResource = platformResource => {
    var _platformResource$, _platformResource$$co;
    const towns = (_platformResource$ = platformResource[0]) === null || _platformResource$ === void 0 ? void 0 : (_platformResource$$co = _platformResource$.content) === null || _platformResource$$co === void 0 ? void 0 : _platformResource$$co.towns;
    setTownsResourceLoading(false);
    if ((towns === null || towns === void 0 ? void 0 : towns.length) > 0) {
      setTowns(towns);
      setStateSelectionRequired(true);
      handleStateChange({
        termsOfService: false
      });
    } else {
      setTowns([]);
      setStateSelectionRequired(false);
    }
  };
  React.useEffect(() => {
    var _values$country, _values$country$value4;
    if ((_values$country = values.country) !== null && _values$country !== void 0 && (_values$country$value4 = _values$country.value) !== null && _values$country$value4 !== void 0 && _values$country$value4.value) {
      var _values$country2, _values$country2$valu;
      setTowns([]);
      loadResources((_values$country2 = values.country) === null || _values$country2 === void 0 ? void 0 : (_values$country2$valu = _values$country2.value) === null || _values$country2$valu === void 0 ? void 0 : _values$country2$valu.value);
    }
  }, [(_values$country3 = values.country) === null || _values$country3 === void 0 ? void 0 : (_values$country3$valu = _values$country3.value) === null || _values$country3$valu === void 0 ? void 0 : _values$country3$valu.value]);
  const getUserData = () => getAllFieldValues(values);
  const {
    fieldErrors,
    isFormInvalid
  } = validateSignUpFields(values);
  const handleTownChange = town => {
    handleStateChange({
      town
    });
    setStateSelectionRequired(false);
  };
  const evalSignupResponse = (cognitoResponse, eligibilityResponse) => {
    if ((eligibilityResponse === null || eligibilityResponse === void 0 ? void 0 : eligibilityResponse.status) === "fulfilled") {
      var _eligibilityResponse$, _eligibilityResponse$2, _eligibilityResponse$3, _eligibilityResponse$4;
      if ((eligibilityResponse === null || eligibilityResponse === void 0 ? void 0 : (_eligibilityResponse$ = eligibilityResponse.value) === null || _eligibilityResponse$ === void 0 ? void 0 : (_eligibilityResponse$2 = _eligibilityResponse$.data) === null || _eligibilityResponse$2 === void 0 ? void 0 : _eligibilityResponse$2.isEligible) === false) {
        // Show SignIn overlay dialog
        throw new Error("UsernameExistsException");
      } else if ((eligibilityResponse === null || eligibilityResponse === void 0 ? void 0 : (_eligibilityResponse$3 = eligibilityResponse.value) === null || _eligibilityResponse$3 === void 0 ? void 0 : (_eligibilityResponse$4 = _eligibilityResponse$3.data) === null || _eligibilityResponse$4 === void 0 ? void 0 : _eligibilityResponse$4.isEligible) === true) {
        if ((cognitoResponse === null || cognitoResponse === void 0 ? void 0 : cognitoResponse.reason) === "UsernameExistsException") {
          // Forward to verification screen
          setVerifySignUp(true);
        } else if (cognitoResponse !== null && cognitoResponse !== void 0 && cognitoResponse.reason) {
          // Show this error on the overlay dialog
          throw new Error(cognitoResponse === null || cognitoResponse === void 0 ? void 0 : cognitoResponse.reason);
        }
      }
    } else {
      // Show this error on the overlay dialog
      throw new Error(eligibilityResponse === null || eligibilityResponse === void 0 ? void 0 : eligibilityResponse.reason);
    }

    // If all good hide the overlay
    setShowOverlay(false);
  };
  const submitHandler = async setLoading => {
    var _values$town, _values$town$value;
    if (((_values$town = values.town) === null || _values$town === void 0 ? void 0 : (_values$town$value = _values$town.value) === null || _values$town$value === void 0 ? void 0 : _values$town$value.isBanned) === true) {
      setVerifySignUp(false);
      handleStateChange({
        error: "BannedState"
      });
      setShowOverlay(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    const userPayload = {
      ...getUserData(),
      groupHash
    };
    try {
      var _values$email;
      createUserPool();
      const promises = [signUpUser(userPayload), getExpertElibility(values === null || values === void 0 ? void 0 : (_values$email = values.email) === null || _values$email === void 0 ? void 0 : _values$email.value)];
      const [cognitoRes, eligibiilityRes] = await Promise.allSettled(promises);
      log.debug(`submitHandler Response: 

                       cognitoRes ${JSON.stringify(cognitoRes, null, 2)}

                       eligibiilityRes ${JSON.stringify(eligibiilityRes, null, 2)}

                       userData: ${JSON.stringify(values, null, 2)}
            `);
      evalSignupResponse(cognitoRes, eligibiilityRes);
      setVerifySignUp(true);
      setShowOverlay(false);
    } catch (err) {
      const errMsg = (err === null || err === void 0 ? void 0 : err.message) || err;
      log.error(`signUp Err: ${errMsg}`);
      handleStateChange({
        error: errMsg
      });
      setShowOverlay(true);
    } finally {
      setLoading(false);
    }
  };
  const handleCountryChange = eventParam => {
    setTownsResourceLoading(true);
    handleStateChange({
      country: eventParam,
      town: {},
      termsOfService: false
    });
  };
  return {
    values,
    loading,
    setLoading,
    disableTermsStyle,
    handleCountryChange,
    getUserData,
    submitHandler,
    handleStateChange,
    towns,
    handleTownChange,
    fieldErrors,
    isFormInvalid,
    showPassword,
    setShowPassword,
    termsDialogOpen,
    showOverlay,
    setShowOverlay,
    verifySignUp,
    setTermsDialogOpen
  };
};